import { plural, t } from '@lingui/macro';

export interface ImportOutcome {
  added: number;
  failed: number;
}

export interface ToastCopy {
  intent: 'success' | 'warning' | 'danger';
  title: string;
  message: string;
}

/**
 * Copy for the three outcomes, per entity.
 *
 * Written out per entity rather than interpolating "Depots"/"Contacts"/
 * "Assets" into one template so each reads naturally and translators get nine
 * real sentences instead of one with a placeholder noun.
 *
 * "Nothing added" covers both an import where every row failed and one that
 * fell over before processing anything; the ticket asks for a single message
 * for both.
 */

const retryMessage = () =>
  t`Try again, or contact support if the issue persists.`;

export function depotToastCopy({ added, failed }: ImportOutcome): ToastCopy {
  if (added === 0) {
    return {
      intent: 'danger',
      title: t`Depots failed to add due to unexpected error.`,
      message: retryMessage(),
    };
  }

  if (failed > 0) {
    return {
      intent: 'warning',
      title: t`Depots partially added.`,
      message: t`${added} added, ${failed} failed. Download the report for details.`,
    };
  }

  return {
    intent: 'success',
    title: t`Depots added successfully.`,
    message: plural(added, {
      one: '# depot was added successfully.',
      other: '# depots were added successfully.',
    }),
  };
}

export function contactToastCopy({ added, failed }: ImportOutcome): ToastCopy {
  if (added === 0) {
    return {
      intent: 'danger',
      title: t`Contacts failed to add due to unexpected error.`,
      message: retryMessage(),
    };
  }

  if (failed > 0) {
    return {
      intent: 'warning',
      title: t`Contacts partially added.`,
      message: t`${added} added, ${failed} failed. Download the report for details.`,
    };
  }

  return {
    intent: 'success',
    title: t`Contacts added successfully.`,
    message: plural(added, {
      one: '# contact was added successfully.',
      other: '# contacts were added successfully.',
    }),
  };
}

export function assetToastCopy({ added, failed }: ImportOutcome): ToastCopy {
  if (added === 0) {
    return {
      intent: 'danger',
      title: t`Assets failed to add due to unexpected error.`,
      message: retryMessage(),
    };
  }

  if (failed > 0) {
    return {
      intent: 'warning',
      title: t`Assets partially added.`,
      message: t`${added} added, ${failed} failed. Download the report for details.`,
    };
  }

  return {
    intent: 'success',
    title: t`Assets added successfully.`,
    message: plural(added, {
      one: '# asset was added successfully.',
      other: '# assets were added successfully.',
    }),
  };
}
